import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthApiService } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';

const passwordPattern = /^(?=.*[A-Za-zА-Яа-яЁё])(?=.*\d).{8,}$/;

const matchPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const a = group.get('password')?.value;
  const b = group.get('passwordConfirm')?.value;
  return a && b && a !== b ? { mismatch: true } : null;
};

@Component({
  selector: 'auth-reset-password',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('reset.heading')" [subtitle]="i18n.t('reset.subtitle')">
      @if (done()) {
        <p class="au-alert au-alert--ok">{{ i18n.t('reset.success') }}</p>
        <a class="au-button" [routerLink]="['/', langPath, 'login']">{{ i18n.t('confirm.go_login') }}</a>
      } @else if (!token) {
        <p class="au-alert au-alert--error">{{ i18n.t('reset.error') || 'Token не передан' }}</p>
      } @else {
        @if (error()) { <p class="au-alert au-alert--error">{{ error() }}</p> }

        <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
          <div class="au-field">
            <label class="au-label" for="password">{{ i18n.t('reset.password') }}</label>
            <input id="password" type="password" autocomplete="new-password" required
                   class="au-input"
                   [class.au-input--error]="invalid('password')"
                   [formControl]="form.controls.password">
            @if (invalid('password')) {
              <span class="au-error">{{ i18n.t('register.password_invalid') }}</span>
            }
          </div>

          <div class="au-field">
            <label class="au-label" for="passwordConfirm">{{ i18n.t('reset.confirm') }}</label>
            <input id="passwordConfirm" type="password" autocomplete="new-password" required
                   class="au-input"
                   [class.au-input--error]="form.errors?.['mismatch'] && form.controls.passwordConfirm.touched"
                   [formControl]="form.controls.passwordConfirm">
            @if (form.errors?.['mismatch'] && form.controls.passwordConfirm.touched) {
              <span class="au-error">{{ i18n.t('register.password_mismatch') }}</span>
            }
          </div>

          <button type="submit" class="au-button" [disabled]="loading()">
            {{ loading() ? i18n.t('common.loading') : i18n.t('reset.submit') }}
          </button>
        </form>
      }
    </auth-card>
  `,
})
export class ResetPasswordComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private api = inject(AuthApiService);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';
  protected token = '';

  readonly form = new FormGroup(
    {
      password: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(passwordPattern)],
      }),
      passwordConfirm: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    },
    { validators: matchPasswords },
  );

  readonly loading = signal(false);
  readonly done = signal(false);
  readonly error = signal<string>('');

  invalid(name: 'password' | 'passwordConfirm'): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.dirty || c.touched);
  }

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.error.set('');
    this.api.resetPassword(this.token, this.form.value.password!).subscribe({
      next: () => { this.done.set(true); this.loading.set(false); },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(err.error?.message || 'Не удалось сменить пароль');
      },
    });
  }
}
